import React from "react";
import "./index.scss";

const LandingPage = () => {
    return (
        <div className="performance-ai">
            {/* ------------------------------ HERO ----------------------------- */}
            <header id="home" className="hero">
                <div className="wrap">
                    <div>
                        <div className="ey">Performance.AI · Powered by EPMTech</div>
                        <h1>More revenue, EBITDA and cash from the business you already have.</h1>
                        <div className="hero-thesis">
                            Changing the Economics of Enterprise Performance Management
                        </div>
                        <p>
                            <b>More Performance. Less Effort.</b> <br/>Performance.AI continuously identifies overlooked performance gaps, drives accountable action and measures financial impact.
                        </p>
                        <a className="btn" href="/performance-ai">
                            Explore Performance.AI
                        </a>
                        <a className="btn ghost" href="contact">
                            Request a Discussion
                        </a>
                    </div>

                    <div className="panel">
                        <b>CONTINUOUS PERFORMANCE LOOP</b>
                        <div>01 &nbsp; Detect overlooked performance gaps</div>
                        <div>02 &nbsp; Diagnose and prioritize opportunities</div>
                        <div>03 &nbsp; Coordinate accountable action</div>
                        <div>04 &nbsp; Measure financial impact</div>
                        <small style={{ color: "#d7e3ef" }}>
                            Illustrative workflow
                        </small>
                    </div>
                </div>
            </header>

            {/* --------------------------- THE CHALLENGE ----------------------- */}
            <section id="challenge" className="gray">
                <div className="wrap">
                    <div className="ey">The challenge</div>
                    <h2>Businesses have data and initiatives — but <br/>performance still leaks.</h2>
                    <p className="intro">
                        Two structural enterprise problems create one outcome: unrealized revenue,
                        margin and cash.
                    </p>
                    <div className="grid">
                        <div className="card">
                            <b>01 · THE PERFORMANCE GAP</b>
                            <h3>Thousands of small opportunities go <br/>unresolved.</h3>
                            <p>
                                Shortfalls across customers, products, locations and processes are
                                individually uneconomical to investigate and resolve manually.
                            </p>
                        </div>
                        <div className="card">
                            <b>02 · THE EXECUTION GAP</b>
                            <h3>Transformation struggles to become results.</h3>
                            <p>
                                Fragmented systems, insights and initiatives make accountability,
                                follow-through and verified financial impact difficult to sustain.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ------------------------ TWO VALUE PROPS ------------------------ */}
            <section id="value">
                <div className="wrap">
                    <div className="ey">Two complementary value propositions</div>
                    <h2>Improve everyday performance. Execute transformation.</h2>
                    <div className="grid">
                        <div className="card">
                            <b>CONTINUOUS PERFORMANCE IMPROVEMENT</b>
                            <h3>Make overlooked opportunities economically viable.</h3>
                            <p>
                                Continuously detect gaps, diagnose causes, prioritize interventions,
                                coordinate corrective action and measure financial results.
                            </p>
                        </div>
                        <div className="card">
                            <b>TRANSFORMATION EXECUTION</b>
                            <h3>Translate strategic programs into realized benefits.</h3>
                            <p>
                                Turn initiatives into accountable operational actions, detect
                                implementation gaps, trigger interventions and measure benefits.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* --------------------------- HOW IT WORKS ------------------------ */}
            <section className="gray">
                <div className="wrap">
                    <div className="ey">How it works</div>
                    <h2>From insight to accountable execution.</h2>
                    <div className="flow">
                        <div>
                            <b>01 DETECT</b>
                            <p>Find overlooked gaps</p>
                        </div>
                        <div>
                            <b>02 UNDERSTAND</b>
                            <p>Diagnose and prioritize</p>
                        </div>
                        <div>
                            <b>03 ACT</b>
                            <p>Assign owners and intervene</p>
                        </div>
                        <div>
                            <b>04 MEASURE</b>
                            <p>Track impact and learn</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------------------- SOLUTION ARCHITECTURE -------------------- */}
            <section id="platform">
                <div className="wrap">
                    <div className="ey">Solution architecture</div>
                    <h2>One AI-native performance layer above existing enterprise systems.</h2>
                    <p className="intro">
                        Use what you have. EACIIT can develop and integrate missing functionality
                        when economically advantageous.
                    </p>
                    <div className="layer dark">
                        <strong>Performance.AI</strong>
                        <p>Continuous Improvement + Transformation Execution</p>
                    </div>
                    <div className="layer">
                        <strong>EPMTech</strong>
                        <p>
                            AI-native data orchestration · Semantic intelligence · Models · Agents ·
                            Workflows · Digital governance
                        </p>
                    </div>
                    <div className="grid">
                        <div className="layer">
                            <strong>Existing client systems</strong>
                            <p>SAP / ERP · CRM · MES · Data Platforms · Specialist Apps</p>
                        </div>
                        <div className="layer" style={{ background: "#fff1e6" }}>
                            <strong>EACIIT-developed capabilities</strong>
                            <p>
                                Demand sensing · Forecasting · Replenishment · Inventory and
                                production optimization
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------------------------- APPROACH --------------------------- */}
            <section id="approach" className="gray">
                <div className="wrap">
                    <div className="ey">Our approach</div>
                    <h2>Start with one opportunity. Prove value. Then scale.</h2>
                    <div className="grid three">
                        <div className="card">
                            <b>01 IDENTIFY</b>
                            <h3>Choose the opportunity</h3>
                            <p>Define a measurable performance gap or transformation priority.</p>
                        </div>
                        <div className="card">
                            <b>02 PROVE</b>
                            <h3>Demonstrate impact</h3>
                            <p>Deploy interventions and measure against an agreed baseline.</p>
                        </div>
                        <div className="card">
                            <b>03 SCALE</b>
                            <h3>Expand selectively</h3>
                            <p>Extend across functions and add capabilities as needed.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* --------------------------- WHY EACIIT -------------------------- */}
            <section>
                <div className="wrap">
                    <div className="ey">Why EACIIT</div>
                    <h2>Established enterprise technology. AI-native architecture.</h2>
                    <div className="grid three">
                        <div className="card">
                            <div className="metric">12+</div>
                            Years of enterprise experience
                        </div>
                        <div className="card">
                            <div className="metric">10+</div>
                            Industries served
                        </div>
                        <div className="card">
                            <div className="metric">30+</div>
                            Client engagements
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------------------------- CONTACT ---------------------------- */}
            <section id="contact" className="cta">
                <div className="wrap">
                    <div className="ey">Let's talk</div>
                    <h2 style={{ color: "white" }}>
                        Where is your business leaving value on the table?
                    </h2>
                    <p>Explore one high-value opportunity in a 30-minute executive discussion.</p>
                    <a
                        className="btn"
                        href="/contact"
                    >
                        Request a Discussion
                    </a>
                    <a
                        className="btn ghost"
                        href="mailto:navdeep@eaciit.com,kapil@eaciit.com?subject=Performance.AI%20Executive%20Discussion"
                    >
                        Contact Growth Team
                    </a>
                </div>
            </section>
        </div>
    );
};

export default LandingPage;